import React, { useReducer, useState } from 'react';
import { Formik, Form } from 'formik';
import {
    UserInputs,
    consultSchema,
    reducer,
    FORM_ACTIONS,
    FORM_PROGRESS,
    CONSULT_FORM_SOURCE,
    initialFormData,
    initialUserData,
} from '.';
import {
    NETLIFY_FUNCTIONS_PATH,
    getFacebookCookies,
    getOptimizelyData,
    validateWithPossibleNow,
    syncPardotTracking,
    getLeadsCaptureEcho,
    isLeadsCaptureResponse,
} from '../../../utils';
import axios from 'axios';
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    FORM_SUBMITTING_MESSAGES,
    AttributionSurvey,
    OptInConsentMsg,
} from '../shared';
import { useScrollToAnchor } from '../../../hooks/useScrollToAnchor.ts';
import { useCloudampDataRef, getCloudampData } from '../../../hooks/useCloudampData';

export function SingleStore({ siteData }) {
    initialFormData.store = siteData.currentMetro.stores[0];
    initialFormData.metro.name = siteData.currentMetro.name;
    initialFormData.metro.stateAbbrev = siteData.currentMetro.stateAbbrev;
    const [formState, dispatch] = useReducer(reducer, initialFormData);

    const isBrowser = typeof window !== 'undefined';
    // Keeps the hidden cloudamp input below populated and mirrored to localStorage.
    const cloudampDataRef = useCloudampDataRef();

    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    // State to hold form values for use in attribution survey
    const [formValues, setFormValues] = useState(null);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });
    const apiEndpoint = `${NETLIFY_FUNCTIONS_PATH}consult-form-handler`;

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('consult-form');
        if (formState.include.formAction === FORM_ACTIONS.selfSchedule) {
            setIsRedirecting(true);
        }
        helpers.setSubmitting(true);
        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();
        try {
            // Validate phone and email with PossibleNow
            const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                values.phone,
                values.email
            );

            const formData = {
                ...values,
                city: formState.metro.name,
                state: formState.metro.stateAbbrev,
                location: formState.store.salesforceValue,
                consultType: formState.include.formAction,
                formSource: CONSULT_FORM_SOURCE,
                updates: true,
                // Clicking a CTA is the marketing opt-in (WEBDEV-4157), so the opt-OUT flag is always false.
                marketing_sms_optout: false,
                cloudamp__data__c: getCloudampData(cloudampDataRef),
                fbp,
                fbc,
                experiment_exposures,
                experiment_user_ID,
                phoneFound,
                wireless,
                emailFound,
                result,
                flags,
            };

            // Submit to Netlify function
            const response = await axios.post(apiEndpoint, formData);

            // formData (not values) is used here because location is merged into formData from
            // formState.store.salesforceValue and is not present on the raw Formik values object.
            // Skipped on the leads-capture path: the lead is already in Salesforce, and syncing a
            // Pardot prospect on top of it would double-write.
            if (!isLeadsCaptureResponse(response)) syncPardotTracking(formData);

            // If backend returned a Self-Schedule URL, perform a top-level redirect
            if (isBrowser && formState.include.formAction === FORM_ACTIONS.selfSchedule && response.data.redirectUrl) {
                helpers.setSubmitting(false);
                helpers.setStatus('Success');
                helpers.resetForm();
                dispatch({
                    type: 'formAction',
                    formAction: '',
                    formProgress: '',
                    user: initialUserData,
                });
                setIsRedirecting(false);
                window.location.href = response.data.redirectUrl;
                return;
            } else {
                setIsRedirecting(false);
                // Store form values for use in attribution survey - only for Ask Question action.
                // The echo carries the ids the handler returned so the survey can reference the lead.
                setFormValues({ formType: 'question', ...formData, ...getLeadsCaptureEcho(response) });
                helpers.setSubmitting(false);
                helpers.setStatus('Success');
                setShowAttributionSurvey(true);
                helpers.resetForm();
                dispatch({
                    type: 'formAction',
                    formAction: '',
                    formProgress: '',
                    user: initialUserData,
                });
            }
        } catch (error) {
            console.error('Form submission error:', error);
            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setIsRedirecting(false);
            setFormValues(null);

            setTimeout(() => {
                helpers.resetForm();
                dispatch({
                    type: 'formAction',
                    formAction: '',
                    formProgress: '',
                    user: initialUserData,
                });
                setShowAttributionSurvey(false);
            }, 5000);
        }
    };

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false);
        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    };

    return (
        <div id="consult-form" className="hr-divider hr-divider-consult">
            <h2 className="text-center subheadsm">Book Your Free Consult</h2>
            <Formik
                initialValues={initialUserData}
                validationSchema={consultSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
            >
                {
                    // This way we can get access to all formik props
                    (formik) => {
                        return (
                            <Form className="container" method="POST">
                                <input ref={cloudampDataRef} type="hidden" name="cloudamp__data__c" />

                                {(formik.isSubmitting || isRedirecting) && (
                                    <FormSubmitting
                                        message={isRedirecting ? FORM_SUBMITTING_MESSAGES.selfSchedule : undefined}
                                    />
                                )}
                                {!isRedirecting && formik.status === 'Failed' && <FormFailed />}
                                {/* Only for Ask Question Action */}
                                {formValues && !formik.isSubmitting && !isRedirecting && formik.status !== 'Failed' && (
                                    <>
                                        {showAttributionSurvey ? (
                                            <div className="row justify-content-center">
                                                <div className="col-12 col-xl-10">
                                                    <AttributionSurvey
                                                        formValues={formValues}
                                                        onComplete={handleAttributionComplete}
                                                        isFullWidth={false}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <FormSuccess />
                                        )}
                                    </>
                                )}

                                {/* Original form content - only shown when not in special states */}
                                {!formik.isSubmitting &&
                                    !isRedirecting &&
                                    formik.status !== 'Failed' &&
                                    !formValues && (
                                        <>
                                            <UserInputs
                                                isQuestion={formState.include.formAction === FORM_ACTIONS.question}
                                                formik={formik}
                                            />

                                            <div className="row justify-content-center mb-3">
                                                <div className="col-xl-10 lh-1 text-start">
                                                    <OptInConsentMsg
                                                        cta={
                                                            // Names the button the user actually clicks: entering
                                                            // question mode swaps the CTA to "Send Question".
                                                            formState.include.formAction === FORM_ACTIONS.question ? (
                                                                'Send Question'
                                                            ) : (
                                                                <>
                                                                    Ask A Question<sup>*</sup> or See Available Times
                                                                    <sup>**</sup>
                                                                </>
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>

                                            <div id="actions-btns" className="row gx-1 gy-4 justify-content-center">
                                                <div className="col-sm-5 col-lg-4 col-xl-3 text-center">
                                                    {
                                                        // No button clicked yet / Initial setup
                                                        formState.include.formProgress ===
                                                        FORM_PROGRESS.initiateQuestion ? (
                                                            <button
                                                                type="submit"
                                                                className="milan-btn milan-navy-btn w-100"
                                                                disabled={!formik.isValid ? true : false}
                                                                onClick={() =>
                                                                    dispatch({
                                                                        type: 'formAction',
                                                                        formAction: FORM_ACTIONS.question,
                                                                        formProgress: FORM_PROGRESS.initiateQuestion, // if this set to '' it won't submit
                                                                        user: formik.values,
                                                                    })
                                                                }
                                                            >
                                                                {formik.isSubmitting
                                                                    ? 'Submitting...'
                                                                    : 'Send Question'}
                                                            </button>
                                                        ) : (
                                                            <span // Don't use button here
                                                                className="milan-btn milan-navy-btn w-100"
                                                                onClick={() =>
                                                                    dispatch({
                                                                        type: 'formAction',
                                                                        formAction: FORM_ACTIONS.question,
                                                                        formProgress: FORM_PROGRESS.initiateQuestion,
                                                                        user: formik.values,
                                                                    })
                                                                }
                                                            >
                                                                Ask A Question <sup>*</sup>
                                                            </span>
                                                        )
                                                    }
                                                </div>

                                                <div className="d-none d-sm-block col-sm-2 col-lg-1 text-center">
                                                    <div className="my-3 my-md-2">
                                                        <strong>OR</strong>
                                                    </div>
                                                </div>

                                                <div className="col-sm-5 col-lg-4 col-xl-3 text-center">
                                                    {formState.include.formProgress !==
                                                    FORM_PROGRESS.initiateQuestion ? (
                                                        <button
                                                            id="local-schedule-btn"
                                                            type="submit"
                                                            className="milan-btn milan-red-btn w-100"
                                                            disabled={!formik.isValid ? true : false}
                                                            onClick={() =>
                                                                dispatch({
                                                                    type: 'formAction',
                                                                    formAction: FORM_ACTIONS.selfSchedule,
                                                                    formProgress: '',
                                                                    user: formik.values,
                                                                })
                                                            }
                                                        >
                                                            {formik.isSubmitting ? (
                                                                'Submitting...'
                                                            ) : (
                                                                <>
                                                                    See Available Times <sup>**</sup>
                                                                </>
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <span // Don't use button here
                                                            className="milan-btn milan-fallback-btn w-100"
                                                            onClick={() =>
                                                                dispatch({
                                                                    type: 'formAction',
                                                                    formAction: '',
                                                                    formProgress: '',
                                                                    user: formik.values,
                                                                })
                                                            }
                                                        >
                                                            Go Back
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                            </Form>
                        );
                    }
                }
            </Formik>
        </div>
    );
}

export default SingleStore;
